import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Typography, Button, Alert, Spin, theme } from 'antd';
import { QRCodeSVG } from 'qrcode.react';
import { SafetyCertificateOutlined, CheckCircleFilled } from '@ant-design/icons';
import { IconLogout } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getAgeVerificationStatus,
  createAgeVerificationSession,
  PENDING_AGE_VERIFICATION_KEY,
  IAgeVerificationSession,
} from '../../services/AgeVerificationService';
import { handleTokenRefresh } from '../../services/ApiService';
import { useAuth } from '../../contextProviders/AuthProvider';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import AppLayout from '../../components/Layouts/Layout';

const { Title, Paragraph } = Typography;

const POLL_INTERVAL_MS = 3000;

/**
 * Post-registration step for accounts on applications that require it (see
 * Application.RequireAgeVerification). Generates a QR code the user scans
 * with their phone — see AgeVerificationController on the backend — and
 * polls for the real, server-persisted result instead of trusting anything
 * set client-side.
 */
const AgeVerification: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [session, setSession] = useState<IAgeVerificationSession | null>(null);
  const [starting, setStarting] = useState(false);
  const [verified, setVerified] = useState(false);
  const [expired, setExpired] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval>>();

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = undefined;
    }
  };

  useEffect(() => stopPolling, []);

  // If the account is already verified (e.g. revisiting this URL directly),
  // skip straight past it.
  useEffect(() => {
    getAgeVerificationStatus()
      .then(async (status) => {
        if (status.verified) {
          localStorage.removeItem(PENDING_AGE_VERIFICATION_KEY);
          // Get a fresh token carrying AgeVerified=true before leaving —
          // RequireAgeVerifiedAttribute on the backend reads that claim.
          await handleTokenRefresh();
          navigate('/dashboard', { replace: true });
        }
      })
      .catch(() => undefined);
  }, [navigate]);

  const startPolling = useCallback((expiresAt: string) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      if (new Date(expiresAt) < new Date()) {
        stopPolling();
        setExpired(true);
        setSession(null);
        return;
      }
      try {
        const status = await getAgeVerificationStatus();
        if (status.verified) {
          stopPolling();
          localStorage.removeItem(PENDING_AGE_VERIFICATION_KEY);
          // Get a fresh token carrying AgeVerified=true before the user
          // continues — RequireAgeVerifiedAttribute on the backend reads
          // that claim, and the current token predates verification.
          await handleTokenRefresh();
          setVerified(true);
        }
      } catch {
        // transient network error — keep polling
      }
    }, POLL_INTERVAL_MS);
  }, []);

  const handleStart = async () => {
    setStarting(true);
    setExpired(false);
    try {
      const newSession = await createAgeVerificationSession();
      setSession(newSession);
      startPolling(newSession.expiresAt);
    } finally {
      setStarting(false);
    }
  };

  const handleContinue = () => {
    navigate('/dashboard');
  };

  return (
    <AppLayout>
      <div style={{ minHeight: '70vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: 16 }}>
        <Card style={{ width: '100%', maxWidth: 480 }}>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <SafetyCertificateOutlined style={{ fontSize: 40, color: token.colorPrimary }} />
          </div>
          <Title level={3} style={{ textAlign: 'center' }}>
            {translate(t, TRANSLATIONS.AGE_VERIFICATION_TITLE)}
          </Title>

          {verified ? (
            <>
              <Alert
                type="success"
                showIcon
                icon={<CheckCircleFilled />}
                message={translate(t, TRANSLATIONS.AGE_VERIFICATION_SUCCESS_TITLE)}
                description={translate(t, TRANSLATIONS.AGE_VERIFICATION_SUCCESS_DESC)}
                style={{ marginBottom: 24 }}
              />
              <Button type="primary" block onClick={handleContinue}>
                {translate(t, TRANSLATIONS.AGE_VERIFICATION_CONTINUE_BUTTON)}
              </Button>
            </>
          ) : session ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                <QRCodeSVG value={session.qrValue} size={220} level="H" />
              </div>
              <Paragraph type="secondary" style={{ textAlign: 'center' }}>
                {translate(t, TRANSLATIONS.AGE_VERIFICATION_SCAN_INSTRUCTIONS)}
              </Paragraph>
              <Alert
                type="info"
                showIcon
                icon={<Spin size="small" />}
                message={translate(t, TRANSLATIONS.AGE_VERIFICATION_WAITING)}
              />
            </>
          ) : (
            <>
              <Paragraph type="secondary" style={{ textAlign: 'center' }}>
                {translate(t, TRANSLATIONS.AGE_VERIFICATION_INTRO)}
              </Paragraph>
              {expired && (
                <Alert
                  type="warning"
                  showIcon
                  message={translate(t, TRANSLATIONS.AGE_VERIFICATION_EXPIRED)}
                  style={{ marginBottom: 16 }}
                />
              )}
              <Button type="primary" block icon={<SafetyCertificateOutlined />} loading={starting} onClick={handleStart}>
                {translate(t, expired ? TRANSLATIONS.AGE_VERIFICATION_RETRY_BUTTON : TRANSLATIONS.AGE_VERIFICATION_START_BUTTON)}
              </Button>
            </>
          )}
        </Card>

        {/* Same mandatory-gate dead end as CompleteProfile.tsx -- ProtectedRoute
            redirects every other route back to /verify-age until the account
            clears it, and on mobile there's otherwise no reachable sign-out
            (AppHeader collapses to logo + bell, MobileBottomNav's tabs all
            bounce back here too). */}
        {!verified && (
          <div style={{ marginTop: 20, textAlign: 'center' }}>
            <Button type="text" icon={<IconLogout size={16} />} onClick={logout} style={{ color: token.colorTextSecondary }}>
              {translate(t, TRANSLATIONS.LOGOUT)}
            </Button>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default AgeVerification;
